import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Card } from "../components/ui/card";
import { ChevronLeft, ChevronRight, Save, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_46ba8d0f-50d1-45b0-932d-164e40ecbfdf/artifacts/l81b46nl_K2B_Logo_Final-2.png";

export default function TokenAssessment() {
    const { token } = useParams();
    const [questions, setQuestions] = useState([]);
    const [responses, setResponses] = useState({});
    const [currentIdx, setCurrentIdx] = useState(0);
    const [status, setStatus] = useState("not_started");
    const [userName, setUserName] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [direction, setDirection] = useState("right");

    useEffect(() => {
        api.get(`/assessment/t/${token}`)
            .then(({ data }) => {
                setQuestions(data.questions || []);
                setStatus(data.status);
                setUserName(data.user_name || "");
                if (data.responses && Object.keys(data.responses).length > 0) {
                    setResponses(data.responses);
                }
            })
            .catch((err) => {
                setError(err.response?.data?.detail || "Invalid or expired assessment link");
            })
            .finally(() => setLoading(false));
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectOption = useCallback((qId, option) => {
        setResponses(prev => ({ ...prev, [String(qId)]: option }));
    }, []);

    const goNext = () => {
        if (currentIdx < questions.length - 1) { setDirection("right"); setCurrentIdx(prev => prev + 1); }
    };
    const goPrev = () => {
        if (currentIdx > 0) { setDirection("left"); setCurrentIdx(prev => prev - 1); }
    };

    const saveProgress = async () => {
        try {
            await api.post(`/assessment/t/${token}/save`, { responses });
            toast.success("Progress saved");
        } catch (err) { toast.error("Failed to save progress"); }
    };

    const submitAssessment = async () => {
        if (Object.keys(responses).length < 25) {
            const unanswered = questions.filter(q => !responses[String(q.id)]).map(q => q.id);
            toast.error(`Please answer all questions. Missing: Q${unanswered.join(", Q")}`);
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/assessment/t/${token}/submit`, { responses });
            setStatus("completed");
            toast.success("Assessment submitted successfully");
        } catch (err) { toast.error("Failed to submit assessment"); }
        finally { setSubmitting(false); }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-background">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-background flex items-center justify-center">
            <Card className="max-w-md w-full mx-4 border-border bg-card p-8 text-center" data-testid="token-error">
                <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                <h2 className="text-xl font-bold text-foreground mb-2" style={{ fontFamily: "Outfit" }}>Access Denied</h2>
                <p className="text-muted-foreground text-sm">{error}</p>
            </Card>
        </div>
    );

    if (status === "completed") return (
        <div className="min-h-screen bg-background flex items-center justify-center">
            <Card className="max-w-lg w-full mx-4 border-border bg-card p-8 text-center" data-testid="token-assessment-complete">
                <div className="flex justify-center mb-6">
                    <div className="p-4 rounded-full" style={{ backgroundColor: "#5BA88E20" }}>
                        <CheckCircle2 className="h-12 w-12" style={{ color: "#5BA88E" }} />
                    </div>
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3" style={{ fontFamily: "Outfit" }}>
                    Assessment Complete
                </h2>
                <p className="text-muted-foreground mb-2">
                    Thank you{userName ? `, ${userName}` : ""}, for completing the Coachability Diagnostic Assessment.
                </p>
                <p className="text-muted-foreground text-sm">
                    Your responses have been submitted and your coach will review the results. You may close this window.
                </p>
            </Card>
        </div>
    );

    const q = questions[currentIdx];
    if (!q) return null;
    const progress = ((Object.keys(responses).length) / questions.length) * 100;
    const selected = responses[String(q.id)];

    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="token-assessment-flow">
            {/* Header */}
            <header className="border-b border-border px-3 sm:px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <img src={LOGO_URL} alt="K2B" className="h-7 w-7 object-contain flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-medium text-muted-foreground truncate" style={{ fontFamily: "Outfit" }}>
                        Coachability Diagnostic
                    </span>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                    {userName && <span className="text-xs text-muted-foreground hidden sm:inline">{userName}</span>}
                    <Button data-testid="token-save-btn" variant="outline" size="sm" onClick={saveProgress} className="gap-1 sm:gap-2">
                        <Save className="h-3 w-3" /> <span className="hidden sm:inline">Save</span>
                    </Button>
                </div>
            </header>

            {/* Progress */}
            <div className="px-3 sm:px-6 py-3 border-b border-border">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Question {currentIdx + 1} of {questions.length}</span>
                    <span className="text-xs text-muted-foreground">{Object.keys(responses).length} of {questions.length} answered</span>
                </div>
                <Progress value={progress} className="h-1.5 progress-glow" />
            </div>

            {/* Question */}
            <div className="flex-1 flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto">
                <div className={`max-w-2xl w-full ${direction === "right" ? "slide-in-right" : "slide-in-left"}`} key={currentIdx}>
                    <div className="mb-8">
                        <span className="text-sm font-medium" style={{ color: "#4A7AB5", fontFamily: "Outfit" }}>Q{q.id}</span>
                        <h2 className="text-xl lg:text-2xl font-medium text-foreground mt-2 leading-relaxed" style={{ fontFamily: "Outfit" }}
                            data-testid={`token-question-text-${q.id}`}>
                            {q.text}
                        </h2>
                    </div>
                    <div className="space-y-3">
                        {Object.entries(q.options).map(([key, text]) => (
                            <button key={key} data-testid={`token-option-${q.id}-${key}`}
                                onClick={() => selectOption(q.id, key)}
                                className={`assessment-option w-full text-left p-4 rounded-lg border transition-all ${
                                    selected === key ? "selected border-[#4A7AB5] bg-[#4A7AB5]/10" : "border-border bg-card hover:border-[#4A7AB5]/50"
                                }`}>
                                <div className="flex gap-3">
                                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                                        selected === key ? "bg-[#4A7AB5] text-white" : "bg-secondary text-muted-foreground"
                                    }`}>{key}</span>
                                    <span className={`text-sm leading-relaxed pt-1 ${selected === key ? "text-foreground" : "text-muted-foreground"}`}>{text}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <footer className="border-t border-border px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between relative z-[10000]" style={{ position: "relative", zIndex: 10000 }}>
                <Button variant="outline" size="sm" onClick={goPrev} disabled={currentIdx === 0} className="gap-1 sm:gap-2">
                    <ChevronLeft className="h-4 w-4" /> <span className="hidden sm:inline">Previous</span>
                </Button>
                <div className="hidden sm:flex gap-1">
                    {questions.map((q, i) => (
                        <button key={q.id || `q-${i}`} onClick={() => { setDirection(i > currentIdx ? "right" : "left"); setCurrentIdx(i); }}
                            className={`w-2 h-2 rounded-full transition-colors ${
                                i === currentIdx ? "bg-[#4A7AB5]" : responses[String(questions[i]?.id)] ? "bg-[#5BA88E]" : "bg-secondary"
                            }`} />
                    ))}
                </div>
                <span className="sm:hidden text-xs font-medium text-muted-foreground">
                    {currentIdx + 1} / {questions.length}
                </span>
                {currentIdx === questions.length - 1 ? (
                    <Button data-testid="token-submit-btn" size="sm" onClick={submitAssessment} disabled={submitting}
                        style={{ backgroundColor: "#F5A623", color: "#FFFFFF" }} className="gap-1 sm:gap-2 font-semibold">
                        {submitting ? "..." : <><Send className="h-4 w-4" /> Submit</>}
                    </Button>
                ) : (
                    <Button size="sm" onClick={goNext} style={{ backgroundColor: "#4A7AB5" }} className="gap-1 sm:gap-2">
                        <span className="hidden sm:inline">Next</span> <ChevronRight className="h-4 w-4" />
                    </Button>
                )}
            </footer>
        </div>
    );
}
